import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Parse Excel file to JSON array
export function parseExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: false });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Parse Portfolio Excel (Final_Sales_Result format)
export function parsePortfolioExcel(file) {
  var MONTH_NAMES = { '01':'January','02':'February','03':'March','04':'April','05':'May','06':'June','07':'July','08':'August','09':'September','10':'October','11':'November','12':'December' };
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: false });
        const sheetName = wb.SheetNames.find(n => n.toLowerCase().includes('final')) || wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const data = raw.map((row, i) => {
          var mk = String(row['MonthKey'] || '');
          return {
          _id: i,
          monthKey: mk,
          login: row['Login'] || '',
          name: String(row['Name'] || ''),
          tDeposit: toNum(row['T.Deposit']),
          tWithdraw: toNum(row['T.Withdraw']),
          tCredit: toNum(row['T.Credit']),
          tVolume: toNum(row['T.Volume']),
          tProfit: toNum(row['T.Profit']),
          deposit: toNum(row['Deposit']),
          withdraw: toNum(row['Withdraw']),
          credit: toNum(row['Credit']),
          profit: toNum(row['Profit']),
          equity: toNum(row['Equity']),
          floatingPL: toNum(row['FloatingPL']),
          equityPrevMonth: toNum(row['EquityPrevMonth']),
          firstDeposit: toNum(row['First_Deposit']),
          volume: toNum(row['Volume']),
          totalDeals: toNum(row['TotalDeals']),
          totalTrades: toNum(row['TotalTrades']),
          country: String(row['Country'] || ''),
          cu: String(row['CU_Account'] || ''),
          crmOpenDate: formatDate(row['CRM_Account_Open_Date']),
          registration: formatDate(row['Registration']),
          lastAccess: formatDate(row['LastAccess']),
          clientStatus: String(row['ClientStatus'] || ''),
          accountType: String(row['Account_Type'] || ''),
          clientType: String(row['ClientType'] || ''),
          agent: String(row['A.M'] || ''),
          tradingMonth: MONTH_NAMES[mk.slice(4, 6)] || String(row['TradingMonth'] || ''),
          tradingYear: mk.slice(0, 4) || String(row['TradingYear'] || ''),
          mostInstrument: String(row['MostInstrument'] || ''),
          mostTradedInstruments: String(row['MostTradedInstruments'] || ''),
          isReactivated: String(row['IsReactivated'] || ''),
          reactivationDate: formatDate(row['ReactivationDate']),
        }});

        resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Parse Accounts Excel (NewAccounts format)
// Columns: Today, Year, Month, CU_Account, Trading_Account_Logins, Client_Name,
//          Phone_Number, CRM_Account_Open_Date, Account_Manager
export function parseAccountsExcel(file) {
  return parseExcel(file).then(raw => {
    return raw.map((row, i) => ({
      _id: 'acc_' + i,
      today: String(row['Today'] || ''),
      id: String(row['CU_Account'] || ''),
      login: row['Login'] || row['Trading_Account_Logins'] || '',
      client: String(row['Name'] || row['Client_Name'] || ''),
      phone: String(row['Phone_Number'] || ''),
      date: formatDate(row['CRM_Account_Open_Date']),
      agent: String(row['Account_Manager'] || ''),
      year: row['Year'] || '',
      month: String(row['Month'] || ''),
      registration: formatDate(row['Registration']),
      lastAccess: formatDate(row['LastAccess']),
      country: String(row['Country'] || ''),
      loginStatus: String(row['LoginStatus'] || ''),
      clientStatus: String(row['ClientStatus'] || ''),
      lifetime: row['Lifetime'] != null ? String(row['Lifetime']) : '',
    }));
  });
}

// Parse FTD Excel - supports both Lifetime (MT5) and legacy FTD_Result format
// Lifetime columns: Login, Name, Registration Time, First Deposit Time, Deposits, First Deposit, ...
// Legacy columns: Year, Month, CU_Account, Trading_Account_Logins, Client_Name, Phone_Number, CRM_Account_Open_Date, FTD_Amount, FTD_Date, Client_Status, Account_Manager
export function parseFTDExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        var buf = new Uint8Array(e.target.result);
        var raw;
        // Detect UTF-16 BOM (FF FE or FE FF) for MT5 CSV/TSV exports
        if ((buf[0] === 0xFF && buf[1] === 0xFE) || (buf[0] === 0xFE && buf[1] === 0xFF)) {
          var decoder = new TextDecoder(buf[0] === 0xFF ? 'utf-16le' : 'utf-16be');
          var text = decoder.decode(buf);
          var lines = text.split(/\r?\n/).filter(function(l) { return l.trim(); });
          // Skip title row (first row is report description, not data)
          var headerIdx = 0;
          for (var hi = 0; hi < Math.min(5, lines.length); hi++) {
            if (lines[hi].indexOf('Login') >= 0 && lines[hi].indexOf('Name') >= 0) { headerIdx = hi; break; }
          }
          var headers = lines[headerIdx].split('\t').map(function(h) { return h.trim(); });
          raw = [];
          for (var li = headerIdx + 1; li < lines.length; li++) {
            var vals = lines[li].split('\t');
            if (vals.length < 2) continue;
            var row = {};
            headers.forEach(function(h, ci) { row[h] = (vals[ci] || '').trim(); });
            raw.push(row);
          }
        } else {
          // Standard Excel/CSV - let SheetJS handle it
          var wb = XLSX.read(buf, { type: 'array', cellDates: false });
          var sheet = wb.Sheets[wb.SheetNames[0]];
          raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          // If first row was a title, try second sheet or re-parse skipping row
          if (raw.length > 0 && !raw[0]['Login'] && !raw[0]['CU_Account'] && !raw[0]['Trading_Account_Logins']) {
            // Try to find header row in first few rows
            var allRows = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 1 });
            for (var ri = 0; ri < Math.min(5, allRows.length); ri++) {
              if (allRows[ri] && allRows[ri].indexOf && (allRows[ri].indexOf('Login') >= 0 || allRows[ri].indexOf('CU_Account') >= 0)) {
                var hdrs = allRows[ri];
                raw = [];
                for (var rj = ri + 1; rj < allRows.length; rj++) {
                  if (!allRows[rj] || !allRows[rj].length) continue;
                  var obj = {};
                  hdrs.forEach(function(h, ci) { obj[h] = allRows[rj][ci] || ''; });
                  raw.push(obj);
                }
                break;
              }
            }
          }
        }

        // Detect format: Lifetime (has 'First Deposit') or Legacy (has 'CU_Account')
        var firstRow = raw[0] || {};
        var isLifetime = ('First Deposit' in firstRow) || ('First Deposit Time' in firstRow);

        var data;
        if (isLifetime) {
          data = raw.map(function(row, i) {
            var ftdDateTime = String(row['First Deposit Time'] || '');
            var ftdDate = ftdDateTime.slice(0, 10).replace(/\./g, '-');
            var ftdTime = ftdDateTime.length > 10 ? ftdDateTime.slice(11, 19) : '';
            var regDateTime = String(row['Registration Time'] || '');
            var regYear = regDateTime.slice(0, 4);
            var isNew = Number(regYear) >= 2026;
            return {
              _id: 'ftd_' + i,
              login: String(row['Login'] || ''),
              client: String(row['Name'] || ''),
              ftdAmount: toNum(row['First Deposit']),
              ftdDate: ftdDate,
              ftdTime: ftdTime,
              deposits: toNum(row['Deposits']),
              regDate: regDateTime.slice(0, 10).replace(/\./g, '-'),
              clientStatus: isNew ? 'New Client' : 'Old Client',
              id: '',
              agent: '',
            };
          }).filter(function(r) { return r.login && r.ftdDate && r.ftdDate !== '-'; });
        } else {
          // Legacy format
          data = raw.map(function(row, i) {
            return {
              _id: 'ftd_' + i,
              id: String(row['CU_Account'] || ''),
              login: String(row['Trading_Account_Logins'] || ''),
              client: String(row['Client_Name'] || ''),
              ftdAmount: toNum(row['FTD_Amount']),
              ftdDate: formatDate(row['FTD_Date']),
              ftdTime: '',
              deposits: 0,
              regDate: '',
              clientStatus: String(row['Client_Status'] || ''),
              agent: String(row['Account_Manager'] || ''),
            };
          });
        }

        resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Parse Leads Excel (flexible format)
export function parseLeadsExcel(file) {
  return parseExcel(file).then(raw => {
    var td = new Date(); var today = td.getFullYear() + '-' + String(td.getMonth() + 1).padStart(2, '0') + '-' + String(td.getDate()).padStart(2, '0');
    return raw.map((row) => ({
      client: String(row['Client Name'] || row['Client_Name'] || row['Name'] || row['Client'] || ''),
      phone: String(row['Phone'] || row['Phone_Number'] || row['Mobile'] || ''),
      agent: String(row['Agent'] || row['Account_Manager'] || row['A.M'] || row['Sales Agent'] || row['Sales_Agent'] || row['Assigned To'] || row['AM'] || row['Sales'] || ''),
      date: today,
      status: '',
      action: '',
      comments: '',
    })).filter(function(r) { return r.client.trim() !== '' || r.phone.trim() !== ''; });
  });
}

// Parse Follow-Up Comments Excel (Old_comments format)
export function parseFollowUpExcel(file) {
  return parseExcel(file).then(raw => {
    var d = new Date();
    var now = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + 'T' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0');
    return raw.map((row) => ({
      cu: String(row['CU_Number'] || row['CU'] || row['CU_Account'] || ''),
      login: String(row['Login'] || ''),
      name: String(row['Name'] || row['Client Name'] || row['Client_Name'] || ''),
      comment: String(row['Comment'] || row['Comments'] || row['Notes'] || ''),
      agent: String(row['SalesAgent'] || row['Sales_Agent'] || row['Agent'] || row['A.M'] || row['Account_Manager'] || ''),
      status: '',
      action: '',
      reminderDate: '',
      reminderTime: '',
      updatedAt: now,
      updatedBy: 'Import',
    })).filter(function(r) { return r.cu.trim() !== '' || r.login.toString().trim() !== ''; });
  });
}

// Parse Not Active Excel (flexible column mapping)
export function parseNotActiveExcel(file) {
  return parseExcel(file).then(function(raw) {
    return raw.map(function(row, i) {
      return {
        _id: 'na_' + i,
        cu: String(row['CU_Account'] || row['CU'] || row['CU Number'] || row['CU_Number'] || ''),
        login: String(row['Login'] || row['Trading_Account_Logins'] || ''),
        name: String(row['Name'] || row['Client Name'] || row['Client_Name'] || ''),
        phone: String(row['Phone'] || row['Phone Number'] || row['Phone_Number'] || ''),
        currentAgent: String(row['Current Agent'] || row['Current_Agent'] || row['A.M'] || row['Account_Manager'] || row['Agent'] || ''),
        newAgent: String(row['New Agent'] || row['New_Agent'] || ''),
        lastAccess: formatDate(row['LastAccess'] || row['Last Access'] || row['Last_Access'] || ''),
        tVolume: toNum(row['T.Volume'] || row['T Volume'] || row['T_Volume'] || row['Volume'] || ''),
        status: String(row['Status'] || ''),
        action: String(row['Action'] || ''),
        comments: String(row['Comments'] || row['Comment'] || ''),
        reminderDate: String(row['Reminder_Date'] || row['Reminder Date'] || ''),
        reminderTime: String(row['Reminder_Time'] || row['Reminder Time'] || ''),
      };
    }).filter(function(r) {
      return r.cu || r.login || r.name;
    });
  });
}

// Export data to Excel
export function exportToExcel(data, filename) {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], { type: 'application/octet-stream' });
  saveAs(blob, filename + '.xlsx');
}

// Backup all system data to one Excel file with multiple sheets
export function backupAllData({ portfolio, accounts, ftd, leads, followups, notactive, users, retention }) {
  const wb = XLSX.utils.book_new();
  const warnings = [];
  const sheetCounts = {};

  // Log initial dataset sizes for debugging
  console.log('Backup input data sizes:', {
    portfolio: portfolio ? portfolio.length : 0,
    accounts: accounts ? accounts.length : 0,
    ftd: ftd ? ftd.length : 0,
    leads: leads ? leads.length : 0,
    followups: followups ? followups.length : 0,
    notactive: notactive ? notactive.length : 0,
    users: users ? users.length : 0
  });

  function safeRows(list, mapper, sheetName) {
    var out = [];
    (list || []).forEach(function(item, idx) {
      try {
        out.push(mapper(item));
      } catch (err) {
        warnings.push(sheetName + ' row ' + idx + ' skipped: ' + (err && err.message ? err.message : 'Unknown error'));
      }
    });
    return out;
  }

  function appendSheetChunked(sheetName, rows, chunkSize) {
    // Split large sheets into chunks to avoid XLSX memory issues
    if (!rows || rows.length === 0) {
      console.log('Skipping sheet "' + sheetName + '": No rows to export');
      return;
    }
    
    chunkSize = chunkSize || 50000; // Default chunk size
    var totalRows = rows.length;
    
    if (totalRows <= chunkSize) {
      // Small sheet - add as single sheet
      try {
        sheetCounts[sheetName] = rows.length;
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName);
        console.log('Added sheet "' + sheetName + '" with ' + rows.length + ' rows');
      } catch (err) {
        warnings.push(sheetName + ' sheet failed: ' + (err && err.message ? err.message : 'Unknown error'));
      }
    } else {
      // Large sheet - split into chunks
      for (var i = 0; i < totalRows; i += chunkSize) {
        var chunk = rows.slice(i, Math.min(i + chunkSize, totalRows));
        var chunkName = sheetName + ' (' + Math.floor(i / chunkSize) + 1 + ')';
        try {
          sheetCounts[chunkName] = chunk.length;
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(chunk), chunkName);
          console.log('Added sheet "' + chunkName + '" with ' + chunk.length + ' rows');
        } catch (err) {
          warnings.push(chunkName + ' sheet failed: ' + (err && err.message ? err.message : 'Unknown error'));
        }
      }
    }
  }

  function appendSheet(sheetName, rows) {
    try {
      if (!rows || rows.length === 0) {
        console.log('Skipping sheet "' + sheetName + '": No rows to export');
        return;
      }
      sheetCounts[sheetName] = rows.length;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName);
      console.log('Added sheet "' + sheetName + '" with ' + rows.length + ' rows');
    } catch (err) {
      warnings.push(sheetName + ' sheet failed: ' + (err && err.message ? err.message : 'Unknown error'));
    }
  }

  // Sheet 0: Dashboard summary
  appendSheet('Dashboard', [{
    Generated_At: new Date().toISOString(),
    Portfolio_Count: (portfolio || []).length,
    New_Accounts_Count: (accounts || []).length,
    FTD_Count: (ftd || []).length,
    Leads_Count: (leads || []).length,
    Not_Active_Count: (notactive || []).length,
    Follow_Up_Count: (followups || []).length,
    Retention_Count: (retention || []).length,
    Users_Count: (users || []).length
  }]);

  // Sheet 1: Portfolio (chunked - can be very large)
  if (portfolio && portfolio.length > 0) {
    const portData = safeRows(portfolio, function(r) {
      return {
        MonthKey: r.monthKey, Login: r.login, Name: r.name, 'T.Deposit': r.tDeposit,
        'T.Withdraw': r.tWithdraw, 'T.Credit': r.tCredit, 'T.Volume': r.tVolume,
        'T.Profit': r.tProfit, Deposit: r.deposit, Withdraw: r.withdraw, Credit: r.credit,
        Profit: r.profit, Equity: r.equity, FloatingPL: r.floatingPL,
        EquityPrevMonth: r.equityPrevMonth, First_Deposit: r.firstDeposit,
        Volume: r.volume, TotalDeals: r.totalDeals,
        TotalTrades: r.totalTrades, Country: r.country,
        CU_Account: r.cu, CRM_Account_Open_Date: r.crmOpenDate, Registration: r.registration,
        LastAccess: r.lastAccess, ClientStatus: r.clientStatus, Account_Type: r.accountType,
        ClientType: r.clientType, 'A.M': r.agent,
        MostInstrument: r.mostInstrument,
        MostTradedInstruments: r.mostTradedInstruments,
        IsReactivated: r.isReactivated || '', ReactivationDate: r.reactivationDate || ''
      };
    }, 'Portfolio');
    appendSheetChunked('Portfolio', portData, 50000);
  }

  // Sheet 2: Accounts
  if (accounts && accounts.length > 0) {
    const accData = safeRows(accounts, function(r) {
      return {
        CU_Account: r.id, Login: r.login, Client_Name: r.client,
        Phone_Number: r.phone, CRM_Account_Open_Date: r.date, Account_Manager: r.agent,
        Registration: r.registration || '', Country: r.country || '',
        LoginStatus: r.loginStatus || '', ClientStatus: r.clientStatus || '',
        Year: r.year, Month: r.month
      };
    }, 'Accounts');
    appendSheetChunked('Accounts', accData, 50000);
  }

  // Sheet 3: FTD
  if (ftd && ftd.length > 0) {
    const ftdData = safeRows(ftd, function(r) {
      return {
        CU_Account: r.id, Login: r.login, Client_Name: r.client,
        FTD_Amount: r.ftdAmount, FTD_Date: r.ftdDate, FTD_Time: r.ftdTime || '',
        Deposits: r.deposits || 0, Registration_Date: r.regDate || '',
        Client_Status: r.clientStatus, Account_Manager: r.agent
      };
    }, 'FTD');
    appendSheet('FTD', ftdData);
  }

  // Sheet 4: Leads
  if (leads && leads.length > 0) {
    const leadsData = safeRows(leads, function(r) {
      return {
        Client_Name: r.client, Phone_Number: r.phone, Account_Manager: r.agent,
        Date: r.date, Status: r.status, Action: r.action, Comments: r.comments,
        Reminder_Date: r.reminderDate || '', Reminder_Time: r.reminderTime || ''
      };
    }, 'Leads');
    appendSheetChunked('Leads', leadsData, 50000);
  }

  // Sheet 5: Follow-Ups
  if (followups && followups.length > 0) {
    const fuData = safeRows(followups, function(r) {
      return {
        CU_Account: r.cu, Login: r.login, Name: r.name, Agent: r.agent,
        Status: r.status, Action: r.action, Comment: r.comment,
        Reminder_Date: r.reminderDate || '', Reminder_Time: r.reminderTime || '',
        Last_Updated: r.updatedAt, Updated_By: r.updatedBy
      };
    }, 'FollowUps');
    appendSheet('FollowUps', fuData);
  }

  // Sheet 6: Not Active
  if (notactive && notactive.length > 0) {
    const notActiveData = safeRows(notactive, function(r) {
      return {
        CU_Account: r.cu || '',
        Login: r.login || '',
        Name: r.name || '',
        Phone_Number: r.phone || '',
        Current_Agent: r.currentAgent || '',
        New_Agent: r.newAgent || '',
        LastAccess: r.lastAccess || '',
        T_Volume: r.tVolume || 0,
        Status: r.status || '',
        Action: r.action || '',
        Comments: r.comments || '',
        Reminder_Date: r.reminderDate || '',
        Reminder_Time: r.reminderTime || ''
      };
    }, 'NotActive');
    appendSheet('NotActive', notActiveData);
  }

  // Sheet 7: Users
  if (users && users.length > 0) {
    const usersData = safeRows(users, function(r) {
      var assignedAgents = Array.isArray(r.assignedAgents)
        ? r.assignedAgents
        : (typeof r.assignedAgents === 'string' && r.assignedAgents.trim() ? [r.assignedAgents] : []);
      return {
        Name: r.name, Email: r.email, Role: r.role,
        Data_Agent_Name: r.dataAgentName || '',
        Assigned_Agents: assignedAgents.join(', ')
      };
    }, 'Users');
    appendSheet('Users', usersData);
  }

  // Sheet 8: Retention
  if (retention && retention.length > 0) {
    const retentionData = safeRows(retention, function(r) {
      return {
        Login: r.login || '',
        Name: r.name || '',
        Country: r.country || '',
        Account_Type: r.accountType || '',
        Reactivation_Date: r.reactivationDate || '',
        Volume: r.volume || 0,
        Agent: r.agent || ''
      };
    }, 'Retention');
    appendSheetChunked('Retention', retentionData, 50000);
  }

  if (wb.SheetNames.length === 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{ Message: 'No data found' }]), 'Info');
  }

  console.log('Backup sheet summary:', sheetCounts);
  console.log('Total sheets created:', wb.SheetNames.length);
  if (warnings.length > 0) {
    console.log('Backup warnings:', warnings);
  }

  var d = new Date();
  var dateStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  try {
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array', compression: true, bookSST: false });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    saveAs(blob, 'CRM_Backup_' + dateStr + '.xlsx');
    return { warnings: warnings, sheetCount: wb.SheetNames.length, sheetCounts: sheetCounts, fileType: 'xlsx' };
  } catch (err) {
    warnings.push('XLSX write failed, fallback to XLSB: ' + (err && err.message ? err.message : 'Unknown error'));
    const bufB = XLSX.write(wb, { bookType: 'xlsb', type: 'array', compression: true, bookSST: false });
    const blobB = new Blob([bufB], { type: 'application/octet-stream' });
    saveAs(blobB, 'CRM_Backup_' + dateStr + '.xlsb');
    return { warnings: warnings, sheetCount: wb.SheetNames.length, sheetCounts: sheetCounts, fileType: 'xlsb' };
  }
}

// Restore data from backup Excel file (reads Leads and FollowUps sheets)
export function parseBackupExcel(file) {
  return new Promise(function(resolve, reject) {
    var reader = new FileReader();
    reader.onload = function(e) {
      try {
        var wb = XLSX.read(e.target.result, { type: 'array', cellDates: false });
        var result = { leads: [], followups: [], portfolio: [], accounts: [], ftd: [] };

        // Restore Leads
        if (wb.SheetNames.indexOf('Leads') >= 0) {
          var leadsRaw = XLSX.utils.sheet_to_json(wb.Sheets['Leads'], { defval: '' });
          result.leads = leadsRaw.map(function(row) {
            return {
              client: String(row['Client_Name'] || ''),
              phone: String(row['Phone_Number'] || ''),
              agent: String(row['Account_Manager'] || ''),
              date: String(row['Date'] || ''),
              status: String(row['Status'] || ''),
              action: String(row['Action'] || ''),
              comments: String(row['Comments'] || ''),
              reminderDate: String(row['Reminder_Date'] || ''),
              reminderTime: String(row['Reminder_Time'] || ''),
            };
          }).filter(function(r) { return r.client.trim() !== '' || r.phone.trim() !== ''; });
        }

        // Restore FollowUps
        if (wb.SheetNames.indexOf('FollowUps') >= 0) {
          var fuRaw = XLSX.utils.sheet_to_json(wb.Sheets['FollowUps'], { defval: '' });
          result.followups = fuRaw.map(function(row) {
            return {
              cu: String(row['CU_Account'] || ''),
              login: String(row['Login'] || ''),
              name: String(row['Name'] || ''),
              agent: String(row['Agent'] || ''),
              status: String(row['Status'] || ''),
              action: String(row['Action'] || ''),
              comment: String(row['Comment'] || ''),
              reminderDate: String(row['Reminder_Date'] || ''),
              reminderTime: String(row['Reminder_Time'] || ''),
              updatedAt: String(row['Last_Updated'] || ''),
              updatedBy: String(row['Updated_By'] || 'Restore'),
            };
          }).filter(function(r) { return r.cu.trim() !== '' || r.login.trim() !== ''; });
        }

        resolve(result);
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Generate leads template - uses embedded pre-built template with agent validation
export function downloadLeadsTemplate() {
  import('./leadsTemplate.js').then(function(mod) {
    var b64 = mod.default;
    var bin = atob(b64);
    var arr = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    var blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Leads_Template.xlsx');
  });
}

// Helper: convert to number safely
function toNum(val) {
  if (val === '' || val === null || val === undefined || val === 'NULL') return 0;
  var n = Number(val);
  return isNaN(n) ? 0 : n;
}

// Helper: format date from various formats
function formatDate(val) {
  if (!val || val === 'NULL' || val === 'None') return '';
  // With cellDates: false, dates come as serial numbers
  if (typeof val === 'number' && val > 25000 && val < 60000) {
    // Use UTC-safe method: serial to UTC date, then extract UTC parts
    var utc = new Date(Date.UTC(1899, 11, 30 + val));
    return utc.getUTCFullYear() + '-' + String(utc.getUTCMonth() + 1).padStart(2, '0') + '-' + String(utc.getUTCDate()).padStart(2, '0');
  }
  if (val instanceof Date) {
    return val.getFullYear() + '-' + String(val.getMonth() + 1).padStart(2, '0') + '-' + String(val.getDate()).padStart(2, '0');
  }
  return String(val).slice(0, 10);
}
